// react
import { ReactNode, useContext, useMemo, useState } from 'react';

// types
import { ActionButton, Pagination, Project } from '../../../../@types';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// context
import { ContextUser } from '../../../../App.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { FilterChangeEvent } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// cards
import { CardEmpty, CardEmptyProps } from '../../../cards/CardEmpty/CardEmpty';
import { CardProject } from '../../../cards/CardProject/CardProject';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { FolderOutlined as IconFolder } from '@mui/icons-material';

// config
import { featureFlags } from '../../../../config/featureFlags';

// i18n
import { useTranslation } from 'react-i18next';

// props
export interface TableProjectsProps {
	CardEmptyProps?: CardEmptyProps;
	hasSearch?: boolean;
	isLoading?: boolean;
	isMock?: boolean;
	isOnboarded?: boolean;
	pagination?: Pagination;
	projects: Project[];
}

export const TableProjects = ({
	CardEmptyProps = {
		empty: {},
	},
	hasSearch = true,
	isLoading,
	isMock = false,
	isOnboarded = true,
	pagination,
	projects,
}: TableProjectsProps) => {
	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCountry = searchParams.get('country');
	const paramQ = searchParams.get('q');

	// defaults
	const defaultFiltersValues = {
		country: 'all',
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		country: paramCountry || defaultFiltersValues.country,
	});

	// context
	const { user } = useContext(ContextUser);

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			isSortable: isOnboarded,
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('id'),
			value: 'idWbg',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('country'),
			value: 'country',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('financing'),
			value: 'financing',
		},
	];

	if (isOnboarded) {
		if (featureFlags['UTILIZATION']) {
			headers.push({
				GridProps: {
					lg: featureFlags['FINANCIAL_TRACEABILITY'] ? 1 : 1.25,
				},
				label: t('utilization'),
				tooltip: {
					title: t('tooltip.utilization'),
				},
				value: 'utilization',
			});
		}

		headers.push({
			GridProps: {
				lg: featureFlags['FINANCIAL_TRACEABILITY'] ? 1 : 1.25,
			},
			label: t('transactionsEvidenced'),
			tooltip: {
				title: t('tooltip.transactionsEvidenced'),
			},
			value: 'traceability',
		});

		if (featureFlags['FINANCIAL_TRACEABILITY']) {
			headers.push({
				GridProps: {
					lg: 1,
				},
				label: t('financialValueEvidenced'),
				tooltip: {
					title: t('tooltip.financialValueEvidenced'),
				},
				value: 'traceability',
			});
		}

		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'updated',
		});
	}

	if (!Object.keys(CardEmptyProps.empty).length) {
		CardEmptyProps.empty = {
			actions: [],
			content: { children: t('empty.projects.content') },
			IconCircleProps: { icon: (<IconFolder />) as ReactNode },
			title: { children: t('empty.projects.title') },
		};

		if (paramQ) {
			CardEmptyProps.empty = {
				...CardEmptyProps.empty,
				content: { children: t('empty.default.content') },
				IconCircleProps: { icon: null },
			};
		}
	}

	if (isOnboarded && user?.role === 'WFA' && CardEmptyProps.empty.actions) {
		CardEmptyProps.empty.actions.push({
			children: t('onboardProject'),
			component: RouterLink,
			to: '/projects/onboard',
			variant: 'contained' as ActionButton['variant'],
		});
	}

	const content = useMemo(() => {
		if (isLoading || !projects.length) {
			return <CardEmpty isLoading={isLoading} {...CardEmptyProps} />;
		}

		return (
			<Grid container spacing={2}>
				{projects.map((project, i) => {
					return (
						<Grid item key={`project-${i}`} xs={12}>
							<CardProject isMock={isMock} isOnboarded={isOnboarded} project={project} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [CardEmptyProps, isLoading, isMock, isOnboarded, projects]);

	return (
		<Grid container spacing={2}>
			{hasSearch && (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
						</Grid>
						<Grid item xs={12} md="auto">
							<BtnFilters
								filters={[
									{
										hasAllOption: true,
										hasNoneOption: false,
										hasSearchParam: true,
										label: t('country'),
										onChange: handleChangeFilterValue,
										type: 'COUNTRY',
										value: filtersValues.country,
										valueType: 'ID',
									},
								]}
								onReset={() => {
									setFiltersValues(defaultFiltersValues);
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			{pagination && (
				<Grid item xs={12}>
					<TableFooter numShowing={projects.length} pagination={pagination} />
				</Grid>
			)}
		</Grid>
	);
};
